import classNames from 'classnames';
import { isNull } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { PAGE_MARGIN_PADDING_BORDER, TIMELINE_WRAPPER, useSplitView } from '../../../../hooks/useSplitView';
import { useWindowResize } from '../../../../hooks/useWindowResize';
import { OpinionScope } from '../../../admin/opinions/store';
import { addOpinionSubCounterpartyType, getAvailableSubCounterpartyTypes, getIsEditingSelectedSubCounterparty, getJurisdictionSubCounterpartyTypes, getSelectedParentCounterparty, getSelectedSubCounterpartyType, getSubCounterpartyScopeFilters, revertOpinionSubCounterparty, selectSubCounterpartyTypesJurisdiction, selectSubCounterpartyTypesParent, updateMultiParentCounterparties, updateOpinionSubCounterparty } from '../../../admin/sub-counterparty-types/store';
import { SubCounterpartyInput } from '../../../admin/sub-counterparty-types/SubCounterpartyInput';
import { FeaturePermission } from '../../../admin/users/store';
import { getUserHasFeaturePermissionNoAdmin } from '../../../auth/login/store';
import { opinionCounterpartyTypeOptions } from '../../../constants/entity';
import { DatasetModal } from '../../../datasets/instances/modal-instance/DatasetModal';
import { Button } from '../../../shared/button/Button';
import { Group } from '../../../shared/icons';
import { ModalHeader } from '../../../shared/modal/ModalHeader';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { OverflowTooltip } from '../../../shared/tooltip';
import { getOpinion, getOpinionAnalysisView } from '../../my-opinions/store';
import { CounterpartyCoverage, toggleSubCounterpartyModal } from '../store';
import styles from './OpinionSection.module.scss';
import { SubCounterpartConfigBody } from './SubCounterpartyConfigBody';

const widthOffset = PAGE_MARGIN_PADDING_BORDER;
const NEW_SUB_COUNTERPARTY_MODAL_WIDTH = 500;
const NEW_SUB_COUNTERPARTY_MODAL_HEIGHT = 500;

interface SubCounterpartyConfigModalProps {
    counterpartyCoverage: CounterpartyCoverage;
    isOpen: boolean;
    close: () => void;
    readOnly: boolean;
    jurisdiction: string;
}

export const SubCounterpartyConfigModal: React.FC<SubCounterpartyConfigModalProps> = ({ isOpen, close, readOnly, counterpartyCoverage, jurisdiction }) => {
    const dispatch = useAppDispatch();
    const opinionAnalysisView = useAppSelector(getOpinionAnalysisView);
    const [screenWidth, screenHeight] = useWindowResize();
    const [width, height] = useSplitView(opinionAnalysisView, widthOffset, TIMELINE_WRAPPER);
    const availableSubCounterpartyTypes = useAppSelector(getAvailableSubCounterpartyTypes);
    const jurisdictionalSubCounterpartyTypes = useAppSelector(getJurisdictionSubCounterpartyTypes);
    const selectedSubCounterparty = useAppSelector(getSelectedSubCounterpartyType);
    const isEditingSubCounterparty = useAppSelector(getIsEditingSelectedSubCounterparty);
    const userHasIndustryStandardPermissions = useAppSelector(getUserHasFeaturePermissionNoAdmin([FeaturePermission.MANAGE_INDUSTRY_STANDARD_OPINIONS]));
    const opinion = useAppSelector(getOpinion);
    const parentCounterparty = useAppSelector(getSelectedParentCounterparty);
    const scopeFilters = useAppSelector(getSubCounterpartyScopeFilters);

    const isIndustryStandardOpinion = useMemo(() => !opinion?.bespoke || false, [opinion]);

    const isSystem = useMemo(() => (userHasIndustryStandardPermissions && isIndustryStandardOpinion) ? 1 : 0, [userHasIndustryStandardPermissions, isIndustryStandardOpinion]);

    const newSubCounterpartyInitialOffset = useMemo(() => {
        const xOffset = screenWidth - (NEW_SUB_COUNTERPARTY_MODAL_WIDTH + 30);
        const yOffset = screenHeight - (NEW_SUB_COUNTERPARTY_MODAL_HEIGHT + 88);
        return { x: xOffset, y: yOffset };
    }, [screenWidth, screenHeight]);

    const newSubCounterpartyScopes = useMemo(() => {
        if (!opinion || isNull(opinion.scope)) {
            return [];
        }
        const ISDA_SCOPES = [OpinionScope.ISDA_NETTING, OpinionScope.COLLATERAL_PROVIDER, OpinionScope.COLLATERAL_TAKER];
        const REPO_SCOPES = [OpinionScope.GMRA_NETTING, OpinionScope.GMSLA_NETTING, OpinionScope.GMRA_GMSLA_NETTING];
        if (ISDA_SCOPES.includes(opinion.scope)) {
            return ISDA_SCOPES;
        }
        if (REPO_SCOPES.includes(opinion.scope)) {
            return REPO_SCOPES;
        }
        return opinion.scope;
    }, [opinion]);

    const addNewSubCounterpartyType = useCallback(() => {
        dispatch(addOpinionSubCounterpartyType(isSystem));
        dispatch(updateOpinionSubCounterparty('jurisdiction', jurisdiction));
        dispatch(updateOpinionSubCounterparty('scopes', newSubCounterpartyScopes));
        if (!isNull(parentCounterparty)) {
            dispatch(updateMultiParentCounterparties([parentCounterparty]));
        }
    }, [dispatch, isSystem, jurisdiction, parentCounterparty, newSubCounterpartyScopes]);

    const setParentCounterpartyType = useCallback((parentCounterparty: string) => dispatch(selectSubCounterpartyTypesParent(parentCounterparty)), [dispatch]);

    const getOptionCount = useCallback((option: string) => jurisdictionalSubCounterpartyTypes.filter(({ parentCounterpartyType, scopes, isSystem }) => (parentCounterpartyType === option) && (scopes.some(scope => scopeFilters.includes(scope))) && (!isIndustryStandardOpinion || (isIndustryStandardOpinion && isSystem === 1))).length, [jurisdictionalSubCounterpartyTypes, scopeFilters, isIndustryStandardOpinion]);
    const closeSubCounterpartyModal = useCallback(() => {
        dispatch(revertOpinionSubCounterparty());
        close();
    }, [dispatch, close]);

    useEffect(() => () => {
        dispatch(selectSubCounterpartyTypesJurisdiction(null));
        dispatch(revertOpinionSubCounterparty());
        dispatch(toggleSubCounterpartyModal(false));
        dispatch(selectSubCounterpartyTypesParent(null));
    }, [dispatch]);

    return (
        <DatasetModal
            isOpen={isOpen}
            closeModal={close}
            isOpinionInstance
        >
            <div className={styles.subCounterpartyTypesModalWrapper} style={{ width, height }}>
                <ModalHeader label='Sub Counterparty Types' icon={Group}></ModalHeader>
                <div className={styles.subCounterpartyTypesModalBody}>
                    <div className={styles.subCounterpartyTypesSelectColumn}>
                        <Scrollable>
                            {opinionCounterpartyTypeOptions.map((counterpartyType, index) => {
                                const counter = getOptionCount(counterpartyType);
                                return (
                                    <div
                                        className={classNames(styles.parentCounterpartyCard, { [styles.selected]: counterpartyType === parentCounterparty })}
                                        onClick={() => setParentCounterpartyType(counterpartyType)}
                                        key={index}>
                                        <OverflowTooltip className={styles.parentCounterpartyCardTooltip} overlayText={counterpartyType} cursor={isEditingSubCounterparty ? 'not-allowed' : undefined} />
                                        {counter > 0 && <div>{`(${counter})`}</div>}
                                    </div>
                                );
                            })}
                        </Scrollable>
                    </div>
                    {isNull(parentCounterparty) ? <div className={styles.subCounterpartyTypesIncludedItemsSectionWrapper}></div> :
                        <SubCounterpartConfigBody
                            counterpartyCoverage={counterpartyCoverage}
                            readOnly={readOnly}
                            availableSubCounterpartyTypes={availableSubCounterpartyTypes}
                            selectedParentCounterpartyType={parentCounterparty}
                            selectedSubCounterparty={selectedSubCounterparty}
                        />
                    }
                </div>
                <div className={styles.subCounterpartyTypesModalButtonWrapper}>
                    <Button onClick={closeSubCounterpartyModal} label='Close' />
                    {!readOnly && <Button onClick={addNewSubCounterpartyType} label='Create New' />}
                </div>
            </div>
            <SubCounterpartyInput
                subCounterpartyTypes={availableSubCounterpartyTypes}
                selectedSubCounterparty={selectedSubCounterparty}
                userHasIndustryStandardPermission={false}
                initialOffset={newSubCounterpartyInitialOffset}
                opinionScope={opinion?.scope}
            />
        </DatasetModal>
    );
};
